import type { ErrorCode, HostChangeReason, Presence } from '@infiltre/shared';
import { Emitter } from '@/shared/lib/driver/emitter';

/**
 * Typed map of UI signals broadcast by the engine driver. These are streams,
 * not state — toast/banner triggers fire once per emit and do not belong in
 * the zustand store. Replays are supported per-kind so a banner that mounts
 * mid-window can pick up the latest payload.
 */
export type UiEventMap = {
  /** Engine/protocol error. `code` is the stable wire-level code; `message` is debug-only fallback. */
  error: { code: ErrorCode; message?: string; at: number };
  'host-changed': {
    newHostId: string;
    previousHostId?: string;
    reason?: HostChangeReason;
    at: number;
  };
  'presence-changed': { playerId: string; presence: Presence; at: number };
};

export type UiEventKind = keyof UiEventMap;

type AnyHandler = (payload: unknown) => void;

class UiEventBus {
  private readonly emitters = new Map<UiEventKind, Emitter<unknown>>();
  private readonly latest = new Map<UiEventKind, unknown>();

  emit<K extends UiEventKind>(kind: K, payload: UiEventMap[K]): void {
    this.latest.set(kind, payload);
    this.emitters.get(kind)?.emit(payload);
  }

  /** Subscribe to a specific event kind. Returns an unsubscribe function. */
  subscribe<K extends UiEventKind>(
    kind: K,
    handler: (payload: UiEventMap[K]) => void,
  ): () => void {
    let emitter = this.emitters.get(kind);
    if (!emitter) {
      emitter = new Emitter<unknown>();
      this.emitters.set(kind, emitter);
    }
    return emitter.on(handler as AnyHandler);
  }

  /** Most recent payload for a kind, or `null` if none ever emitted. */
  last<K extends UiEventKind>(kind: K): UiEventMap[K] | null {
    return (this.latest.get(kind) as UiEventMap[K] | undefined) ?? null;
  }

  /** Test helper — wipes subscribers + last-value cache. */
  reset(): void {
    for (const e of this.emitters.values()) e.clear();
    this.emitters.clear();
    this.latest.clear();
  }
}

export const uiEventBus = new UiEventBus();

/** Test helper. Re-exported under a stable name so test setup files can wipe state. */
export const resetUiEventBus = (): void => {
  uiEventBus.reset();
};
